import qs from "qs";

/**
 * FilterOptions as a flat array.
 *
 * @param {array} filters Filters.
 * @param {boolean} selected Selected items only.
 */
export function filterOptionsFlatArray(filters, selected = true) {
    if (!Array.isArray(filters)) return false;
    const flatArray =
        filters.length &&
        filters
            .filter(({ enabled }) => enabled)
            .map(({ values }) => values)
            .filter(v => v.length) // some options, LGTP, had no values (options/filters)
            .reduce((flatArray, option) => flatArray.concat(option));

    return selected && Array.isArray(flatArray)
        ? flatArray.filter(({ selected }) => selected)
        : flatArray;
}

/**
 * Extract Filter Option hashes from the URL.
 *
 * @param {string} path URL path.
 *
 * @returns {array}
 */
export function extractFilterOptionsFromUrl(path) {
    const pathExploded = path.split("/");
    const hashSegment = pathExploded[pathExploded.length - 1];
    const filterSegmentPattern = /^F-([A-Z_]+[0-9]+)+/;
    const filterOptionPattern = /([A-Z_]+[0-9]+)+/;
    const hasFilters = hashSegment.match(filterSegmentPattern);

    let mutableHashSegment = hashSegment;
    let match = "";
    let hashes = [];

    if (hasFilters) {
        while (mutableHashSegment.match(filterOptionPattern)) {
            match = mutableHashSegment.match(filterOptionPattern);
            const matchFound = match[1] || false;
            if (matchFound) {
                mutableHashSegment = mutableHashSegment.slice(
                    0,
                    mutableHashSegment.length - matchFound.length
                );
                hashes.push(matchFound);
            }
        }
    }

    return hashes.reverse();
}

/**
 * Create hash segment of URL ppath.
 *
 * @param {array} filters Filters.
 */
export function createHashesUrlSegment(filters) {
    const optionsFlattened = filterOptionsFlatArray(filters, true);

    return optionsFlattened.length
        ? "F-" + optionsFlattened.map(({ hash }) => hash).join("")
        : "";
}

/**
 * Create SEO segment of URL path.
 *
 * @param {array} filters Filters.
 */
export function createSeoUrlSegment(filters) {
    const selectedOptions = filterOptionsFlatArray(filters) || [];
    return selectedOptions
        .map(({ value }) => value.replace(/[\s&]/g, "-").replace(/\//g, ""))
        .join("/");
}

export function getHashPrefix(hash) {
    const preHash = hash.replace(/\d+$/, "");
    return preHash;
}

/**
 * Create public URL for client-side navigation.
 *
 * @param {string} type Door type.
 * @param {Object[]} filters Filters.
 * @param {Object[]} nextFilterOptions Filter mutations.
 * @param {number} page Page number.
 */
export function createPublicUrlPath(
    pathPrefix,
    filters,
    nextFilterOptions = [],
    page = 1,
    locationSearch
) {
    const singleSelectFilters = ["LDOOR_APPLICATION", "LDOOR_GLASS"];

    let mergedFilters = filters.map(filter => ({
        ...filter,
        values: filter.values.map(value => {
            return {
                ...value,
                selected: nextFilterOptions.map(({ hash }) => hash).includes(value.hash)
                    ? nextFilterOptions.find(({ hash }) => hash === value.hash).selected
                    : value.selected
            };
        })
    }));

    nextFilterOptions
        .filter(filterOption => {
            return singleSelectFilters.includes(getHashPrefix(filterOption.hash));
        })
        .forEach(filterOption => {
            mergedFilters = mergedFilters.map(filter => {
                return {
                    ...filter,
                    values:
                        filter.hash_prefix === getHashPrefix(filterOption.hash)
                            ? filter.values.map(value => {
                                return {
                                    ...value,
                                    selected:
                                        filterOption.hash === value.hash
                                }
                            })
                            : filter.values
                };
            });
        });

    const seoSegment = createSeoUrlSegment(mergedFilters);
    const hashSegment = createHashesUrlSegment(mergedFilters);

    //Manipulate location.search string and add to end of pageQuery
    locationSearch = locationSearch ? locationSearch : "";
    let searchMinusPage = locationSearch.replace('?', "")
    searchMinusPage = searchMinusPage.replace('&p=' + page, "")
    searchMinusPage = searchMinusPage.replace('p=' + page, "")

    if(searchMinusPage !== "" & page > 1 & searchMinusPage.substring(0,1) !== "&"){
        searchMinusPage = "&" + searchMinusPage
    }

    if(page < 2){
        searchMinusPage = "?" + searchMinusPage
    }

    //Prevents double tracking of main page
    if(searchMinusPage === "?"){
        searchMinusPage = ""
    }


    const pageQuery = page > 1 ? `?p=${page}` + searchMinusPage : searchMinusPage;

    return (
        [pathPrefix, seoSegment, hashSegment].filter(v => v).join("/") + pageQuery
    );
}

/**
 * Get the page from the location object.
 *
 * Extracts the page number from the search query in the window.location object.
 *
 * @param {Object} location
 */
export function getPageFromLocation(location) {
    const params = new URLSearchParams(location.search)
    const page = Number(params.get('p'));
    return page ? page : 1;
}
